import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  rem,
  Table,
  ScrollArea,
  Select,
  Button,
  Badge,
  TextInput,
  Flex,
  Loader,
  Alert,
  Pagination
} from '@mantine/core';
import { YearPickerInput, MonthPickerInput } from '@mantine/dates';
import {
  IconCalendar,
  IconSearch,
  IconDownload,
  IconCoin,
  IconShoppingCart,
  IconWallet,
  IconArrowUp,
  IconArrowDown,
  IconSelector
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-tw';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import firestore from '../../utils/firestore';
import format_with_substring from '../../utils/number';

// 設置 dayjs 語言
dayjs.locale('zh-tw');

const DetailedTable = () => {
  const [showDate, setShowDate] = useState(dayjs());
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalIncome: 0,
    totalSpecialIncome: 0,
    totalExpenses: 0,
    balance: 0,
    recordCount: 0
  });
  
  // 排序和分頁狀態
  const [sortBy, setSortBy] = useState('date'); // date, income, expense, net
  const [sortOrder, setSortOrder] = useState('asc'); // asc, desc
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20); // 每頁顯示20筆記錄

  const year = showDate.year();
  const month = selectedMonth ? dayjs(selectedMonth).month() : null;

  // 獲取資料
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      let q;
      if (month !== null) {
        // 查詢特定月份的資料
        q = query(
          collection(firestore, 'accounting'),
          where('year', '==', year),
          where('month', '==', month),
          orderBy('day', 'asc')
        );
      } else {
        // 查詢整年資料
        q = query(
          collection(firestore, 'accounting'),
          where('year', '==', year),
          orderBy('month', 'asc'),
          orderBy('day', 'asc')
        );
      }

      try {
        const accounting = await getDocs(q);
        
        if (accounting.docs.length > 0) {
          const records = [];
          accounting.forEach(doc => {
            const docData = doc.data();
            records.push({
              id: doc.id,
              ...docData,
              // 確保數值欄位是數字
              income: Number(docData.income) || 0,
              specialIncome: Number(docData.specialIncome) || 0,
              expenses: Number(docData.expenses) || 0,
            });
          });
          setData(records);
        } else {
          setData([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('載入資料時發生錯誤，請稍後重試');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [year, month]);

  // 過濾資料
  useEffect(() => {
    let filtered = data;

    // 搜尋過濾
    if (searchTerm) {
      filtered = filtered.filter(record => 
        (record.vendor && record.vendor.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.specialIncomeFrom && record.specialIncomeFrom.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (record.memo && record.memo.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // 類型過濾
    if (filterType !== 'all') {
      filtered = filtered.filter(record => {
        if (filterType === 'income') {
          return (record.income > 0 || record.specialIncome > 0) && record.expenses === 0;
        } else if (filterType === 'expense') {
          return record.expenses > 0;
        } else if (filterType === 'mixed') {
          return (record.income > 0 || record.specialIncome > 0) && record.expenses > 0;
        }
        return true;
      });
    }

    setFilteredData(filtered);
    calculateStats(filtered);
  }, [data, searchTerm, filterType]);

  // 計算統計
  const calculateStats = (records) => {
    const totalIncome = records.reduce((sum, record) => sum + record.income, 0);
    const totalSpecialIncome = records.reduce((sum, record) => sum + record.specialIncome, 0);
    const totalExpenses = records.reduce((sum, record) => sum + record.expenses, 0);
    
    setStats({
      totalIncome,
      totalSpecialIncome,
      totalExpenses,
      balance: totalIncome + totalSpecialIncome - totalExpenses,
      recordCount: records.length
    });
  };

  // 格式化日期
  const formatDate = (year, month, day) => {
    return `${year}/${String(month + 1).padStart(2, '0')}/${String(day).padStart(2, '0')}`;
  };

  // 取得農曆資訊 (簡化版本)
  const getLunarInfo = (year, month, day) => {
    // 這裡可以整合農曆轉換庫，暫時返回空字串
    return '';
  };

  // 排序函數
  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1); // 重置到第一頁
  };

  // 獲取排序後的資料
  const getSortedData = () => {
    const sorted = [...filteredData].sort((a, b) => {
      let valueA, valueB;
      
      switch (sortBy) {
        case 'date':
          valueA = new Date(a.year, a.month, a.day);
          valueB = new Date(b.year, b.month, b.day);
          break;
        case 'income':
          valueA = a.income + a.specialIncome;
          valueB = b.income + b.specialIncome;
          break;
        case 'expense':
          valueA = a.expenses;
          valueB = b.expenses;
          break;
        case 'net':
          valueA = (a.income + a.specialIncome) - a.expenses;
          valueB = (b.income + b.specialIncome) - b.expenses;
          break;
        default:
          return 0;
      }
      
      if (valueA < valueB) return sortOrder === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  // 獲取分頁後的資料
  const getPaginatedData = () => {
    const sortedData = getSortedData();
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  };

  // 獲取總頁數
  const getTotalPages = () => {
    return Math.ceil(filteredData.length / pageSize);
  };

  // 重置到第一頁當過濾條件改變時
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterType]);

  // 排序圖標組件
  const SortIcon = ({ column }) => {
    if (sortBy !== column) {
      return <IconSelector size={14} style={{ opacity: 0.3 }} />;
    }
    return sortOrder === 'asc' ? 
      <IconArrowUp size={14} color="blue" /> : 
      <IconArrowDown size={14} color="blue" />;
  };

  // 匯出資料
  const exportData = () => {
    // 這裡可以實作匯出功能，例如 CSV 或 Excel
    console.log('Export data:', filteredData);
  };

  return (
    <Container size="xl" p={0}>
      <Stack gap={{ base: 'md', sm: 'xl' }}>
        {/* 標題與控制項 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }} bg="blue.0">
          <Stack align="center" gap="md">
            <Title order={1} size="xl" c="blue.7">
              收支明細表
            </Title>
            
            <Group justify="center" gap="lg" wrap="wrap">
              <YearPickerInput
                label="年份"
                placeholder="選擇年份"
                value={showDate.toDate()}
                onChange={(date) => {
                  if (date) {
                    setShowDate(dayjs(date));
                  }
                }}
                size="md"
                leftSection={<IconCalendar size={16} />}
                styles={{
                  label: { fontSize: rem(14), fontWeight: 600 },
                  input: { fontSize: rem(14) }
                }}
              />

              <MonthPickerInput
                label="月份 (可選)"
                placeholder="選擇月份或顯示全年"
                value={selectedMonth}
                onChange={setSelectedMonth}
                size="md"
                clearable
                leftSection={<IconCalendar size={16} />}
                styles={{
                  label: { fontSize: rem(14), fontWeight: 600 },
                  input: { fontSize: rem(14) }
                }}
              />
            </Group>
          </Stack>
        </Paper>

        {/* 統計概覽 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
          <Title order={2} size="lg" mb="md" c="blue.7">
            統計概覽
          </Title>
          
          <Group justify="space-around" wrap="wrap">
            <Stack align="center" gap="xs">
              <IconCoin size={24} color="#51cf66" />
              <Text size="sm" c="dimmed" fw={600}>一般收入</Text>
              <Text size="lg" fw={700} c="green.6">
                ${stats.totalIncome.toLocaleString()}
              </Text>
            </Stack>

            <Stack align="center" gap="xs">
              <IconCoin size={24} color="#339af0" />
              <Text size="sm" c="dimmed" fw={600}>特殊收入</Text>
              <Text size="lg" fw={700} c="blue.6">
                ${stats.totalSpecialIncome.toLocaleString()}
              </Text>
            </Stack>

            <Stack align="center" gap="xs">
              <IconShoppingCart size={24} color="#fa5252" />
              <Text size="sm" c="dimmed" fw={600}>支出</Text>
              <Text size="lg" fw={700} c="red.6">
                ${stats.totalExpenses.toLocaleString()}
              </Text>
            </Stack>

            <Stack align="center" gap="xs">
              <IconWallet size={24} color={stats.balance >= 0 ? "#339af0" : "#fa5252"} />
              <Text size="sm" c="dimmed" fw={600}>淨結餘</Text>
              <Text size="lg" fw={700} c={stats.balance >= 0 ? "blue.6" : "red.6"}>
                ${stats.balance.toLocaleString()}
              </Text>
            </Stack>

            <Stack align="center" gap="xs">
              <Text size="sm" c="dimmed" fw={600}>記錄筆數</Text>
              <Badge size="lg" color="gray" variant="light">
                {stats.recordCount}
              </Badge>
            </Stack>
          </Group>
        </Paper>

        {/* 錯誤顯示 */}
        {error && (
          <Alert color="red" title="載入錯誤" variant="filled">
            {error}
          </Alert>
        )}
        
        {/* 搜尋與過濾 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
          <Flex
            gap="md"
            align="end"
            wrap="wrap"
            justify="space-between"
          >
            <Group gap="md" flex={1}>
              <TextInput
                label="搜尋"
                placeholder="搜尋廠商、來源或備註..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.currentTarget.value)}
                leftSection={<IconSearch size={16} />}
                style={{ minWidth: 200 }}
              />

              <Select
                label="篩選類型"
                data={[
                  { value: 'all', label: '全部' },
                  { value: 'income', label: '純收入' },
                  { value: 'expense', label: '純支出' },
                  { value: 'mixed', label: '收支並存' },
                ]}
                value={filterType}
                onChange={setFilterType}
                style={{ minWidth: 120 }}
              />
            </Group>

            <Button
              leftSection={<IconDownload size={16} />}
              onClick={exportData}
              variant="light"
            >
              匯出資料
            </Button>
          </Flex>
        </Paper>

        {/* 資料表格 */}
        <Paper shadow="md" radius="lg" p={{ base: 'md', sm: 'xl' }}>
          <Title order={2} size="lg" mb="md" c="blue.7">
            明細記錄 ({filteredData.length} 筆)
          </Title>
          
          <ScrollArea>
            <Table fontSize="sm" withRowBorders withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th 
                    style={{ textAlign: 'center', minWidth: rem(100), cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('date')}
                  >
                    <Group justify="center" gap={4}>
                      <Text fw={600}>日期</Text>
                      <SortIcon column="date" />
                    </Group>
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'center', minWidth: rem(80) }}>農曆</Table.Th>
                  <Table.Th 
                    style={{ textAlign: 'center', minWidth: rem(100), cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('income')}
                  >
                    <Group justify="center" gap={4}>
                      <Text fw={600}>總收入</Text>
                      <SortIcon column="income" />
                    </Group>
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'center', minWidth: rem(90) }}>特殊收入</Table.Th>
                  <Table.Th style={{ textAlign: 'center', minWidth: rem(100) }}>來源</Table.Th>
                  <Table.Th 
                    style={{ textAlign: 'center', minWidth: rem(80), cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('expense')}
                  >
                    <Group justify="center" gap={4}>
                      <Text fw={600}>支出</Text>
                      <SortIcon column="expense" />
                    </Group>
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'center', minWidth: rem(80) }}>廠商</Table.Th>
                  <Table.Th 
                    style={{ textAlign: 'center', minWidth: rem(80), cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => handleSort('net')}
                  >
                    <Group justify="center" gap={4}>
                      <Text fw={600}>淨額</Text>
                      <SortIcon column="net" />
                    </Group>
                  </Table.Th>
                  <Table.Th style={{ textAlign: 'center', minWidth: rem(100) }}>備註</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loading ? (
                  <Table.Tr>
                    <Table.Td colSpan={9} style={{ textAlign: 'center', padding: rem(40) }}>
                      <Loader size="lg" variant="dots" />
                      <Text size="lg" c="dimmed" mt="md">
                        載入資料中...
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : filteredData.length > 0 ? (
                  getPaginatedData().map((record, index) => {
                    const totalIncome = record.income + record.specialIncome;
                    const netAmount = totalIncome - record.expenses;
                    
                    return (
                      <Table.Tr key={record.id || index}>
                        <Table.Td style={{ textAlign: 'center', fontWeight: 500 }}>
                          {formatDate(record.year, record.month, record.day)}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center', fontSize: rem(12), color: '#666' }}>
                          {getLunarInfo(record.year, record.month, record.day)}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {totalIncome > 0 ? (
                            <Text c={totalIncome === record.income ? "green.6" : "teal.6"} fw={500}>
                              {format_with_substring(totalIncome)}
                            </Text>
                          ) : ''}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {record.specialIncome > 0 ? (
                            <Text c="blue.6" fw={500}>
                              {format_with_substring(record.specialIncome)}
                            </Text>
                          ) : ''}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Text size="sm" c="blue.7">
                            {record.specialIncomeFrom || ''}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          {record.expenses > 0 ? (
                            <Text c="red.6" fw={500}>
                              {format_with_substring(record.expenses)}
                            </Text>
                          ) : ''}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Text size="sm" c="orange.7">
                            {record.vendor || ''}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right', fontWeight: 600 }}>
                          <Text c={netAmount >= 0 ? 'blue.6' : 'red.6'}>
                            {netAmount !== 0 ? format_with_substring(netAmount) : ''}
                          </Text>
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'left' }}>
                          <Text size="sm" c="gray.7">
                            {record.memo || ''}
                          </Text>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                ) : (
                  <Table.Tr>
                    <Table.Td colSpan={9} style={{ textAlign: 'center', padding: rem(40) }}>
                      <Text size="lg" c="dimmed">
                        沒有找到符合條件的記錄
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                )}
              </Table.Tbody>
            </Table>
          </ScrollArea>
          
          {/* 分頁控制器 */}
          {filteredData.length > pageSize && (
            <Stack gap="md" mt="lg">
              <Group justify="space-between" align="center">
                <Text size="sm" c="dimmed">
                  顯示第 {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, filteredData.length)} 筆，
                  共 {filteredData.length} 筆記錄
                </Text>
                <Group gap="xs">
                  <Text size="sm" c="dimmed">
                    每頁 {pageSize} 筆
                  </Text>
                  <Badge variant="light" color="blue">
                    第 {currentPage} / {getTotalPages()} 頁
                  </Badge>
                </Group>
              </Group>
              
              <Group justify="center">
                <Pagination
                  value={currentPage}
                  onChange={setCurrentPage}
                  total={getTotalPages()}
                  size="md"
                  withEdges
                  siblings={2}
                  boundaries={1}
                />
              </Group>
            </Stack>
          )}
        </Paper>
      </Stack>
    </Container>
  );
};

export default DetailedTable;
